/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";
import AppContext from "AppContext";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Page from "components/common_components/Page/Page";
import { notifyError, notifyInfo } from "redux/actions/Notifications";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import LoadingPage from "components/common_components/LoadingPage";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import Status from "components/common_components/Status";
import { Grid, TextField, MenuItem, Collapse, IconButton, Tooltip } from "@material-ui/core";
import ApplyPaymentDialog from "./ApplyPaymentDialog";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";

const PAYMENT_STATUSES = ["PENDING", "FAILED", "SUCCEEDED", "REFUNDED"];
const APPLICATION_STATUSES = ["PENDING", "PARTIAL", "COMPLETED"];

const DEFAULT_SORT = "payment_creation_datetime,desc";
const DEFAULT_PAGE_SIZE = 15;

const DEFAULT_FILTERS = {
    clientFullName: "",
    clientId: "",
    paymentId: "",
    status: "SUCCEEDED",
    applicationStatus: "",
    amountPaidUsdFrom: "",
    amountPaidUsdTo: "",
    dateFrom: null,
    dateTo: null,
};

const paymentStatusMap = {
    PENDING: { category: "warning", label: "Pending" },
    FAILED: { category: "error", label: "Failed" },
    SUCCEEDED: { category: "success", label: "Succeeded" },
    REFUNDED: { category: "info", label: "Refunded" },
};

const applicationStatusMap = {
    PENDING: { category: "warning", label: "Pending" },
    PARTIAL: { category: "info", label: "Partial" },
    COMPLETED: { category: "success", label: "Completed" },
};

function buildApiParams(filtersToUse, page, size, sort) {
    const params = {};
    if (filtersToUse.clientFullName) params.clientFullName = filtersToUse.clientFullName;
    if (filtersToUse.clientId) params.clientId = filtersToUse.clientId;
    if (filtersToUse.paymentId) params.paymentId = filtersToUse.paymentId;
    if (filtersToUse.status) params.status = filtersToUse.status;
    if (filtersToUse.applicationStatus) params.applicationStatus = filtersToUse.applicationStatus;
    if (filtersToUse.amountPaidUsdFrom) params.amountPaidUsdFrom = filtersToUse.amountPaidUsdFrom;
    if (filtersToUse.amountPaidUsdTo) params.amountPaidUsdTo = filtersToUse.amountPaidUsdTo;
    if (filtersToUse.dateFrom) params.dateFrom = moment(filtersToUse.dateFrom).toISOString();
    if (filtersToUse.dateTo) params.dateTo = moment(filtersToUse.dateTo).toISOString();
    params.page = page;
    params.size = size;
    params.sort = sort;
    return params;
}

function ClientPortalPayments() {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const restoredFilters = location.state?.cpPaymentsFilters;

    const [data, setData] = useState([]);
    const [pageableData, setPageableData] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState(restoredFilters || { ...DEFAULT_FILTERS });
    const [committedFilters, setCommittedFilters] = useState(restoredFilters || { ...DEFAULT_FILTERS });
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [applyDialogOpen, setApplyDialogOpen] = useState(false);
    const [applyPayment, setApplyPayment] = useState(null);

    const sortRef = useRef(DEFAULT_SORT);
    const sizeRef = useRef(DEFAULT_PAGE_SIZE);

    const {
        api: {
            General: { ClientPortalPayments: CpPaymentsApi },
        },
    } = useContext(AppContext);

    function fetchData(filtersToUse, page, size, sort, showLoader = true) {
        if (showLoader) setIsLoading(true);
        sortRef.current = sort;
        sizeRef.current = size;
        CpPaymentsApi.getList({
            onSuccess: (responseData, pageable) => {
                setData(responseData);
                setPageableData(pageable);
                setIsLoading(false);
            },
            onInfo: (info, pageable) => {
                setData([]);
                dispatch(notifyInfo(info));
                setPageableData(pageable);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setData([]);
                setIsLoading(false);
            },
            data: buildApiParams(filtersToUse, page, size, sort),
        });
    }

    function getCpPayments(requestData, showLoader = true) {
        let page = 0;
        let size = sizeRef.current;
        let sort = sortRef.current;

        if (requestData) {
            page = requestData.pageNumber ?? 0;
            size = requestData.pageSize ?? DEFAULT_PAGE_SIZE;
            if (requestData.orderBy) {
                sort = `${requestData.orderBy},${requestData.orderDirection || "desc"}`;
            }
        }

        fetchData(committedFilters, page, size, sort, showLoader);
    }

    useEffect(() => {
        fetchData(committedFilters, 0, DEFAULT_PAGE_SIZE, DEFAULT_SORT);
        return () => setData([]);
    }, []);

    function onSearchHandler() {
        const newCommitted = { ...filters };
        setCommittedFilters(newCommitted);
        fetchData(newCommitted, 0, sizeRef.current, sortRef.current);
    }

    function onClearFiltersHandler() {
        const cleared = { ...DEFAULT_FILTERS };
        setFilters(cleared);
        setCommittedFilters(cleared);
        fetchData(cleared, 0, DEFAULT_PAGE_SIZE, DEFAULT_SORT);
    }

    function onFilterChange(field, value) {
        setFilters((prev) => ({ ...prev, [field]: value }));
    }

    function onRowClickHandler(row) {
        history.push(`/cp-payments/${row.id}`, {
            cpPayment: row,
            cpPaymentsFilters: committedFilters,
        });
    }

    function onApplyClickHandler(row) {
        setApplyPayment(row);
        setApplyDialogOpen(true);
    }

    function onApplyDialogClose() {
        setApplyDialogOpen(false);
        setApplyPayment(null);
    }

    function onApplySuccess() {
        fetchData(committedFilters, 0, sizeRef.current, sortRef.current);
    }

    const headCells = [
        {
            index: 0,
            id: "clientFullName",
            label: "Client",
            orderBy: "client_full_name",
            sortable: false,
        },
        {
            index: 1,
            id: "amountPaidUsd",
            label: "Amount",
            orderBy: "amount_paid_usd_cents",
            type: "currency",
        },
        {
            index: 2,
            id: "status",
            label: "Status",
            orderBy: "status",
            render: (_, { value }) => {
                const mapped = paymentStatusMap[value];
                if (mapped) {
                    return <Status id={value.toLowerCase()} category={mapped.category} label={mapped.label} />;
                }
                return value;
            },
        },
        {
            index: 3,
            id: "paymentCreationDatetimeString",
            label: "Date",
            orderBy: "payment_creation_datetime",
        },
        {
            index: 4,
            id: "applicationStatus",
            label: "Application",
            orderBy: "application_status",
            render: (_, { value }) => {
                const mapped = applicationStatusMap[value];
                if (mapped) {
                    return <Status id={(value || "").toLowerCase()} category={mapped.category} label={mapped.label} />;
                }
                return value || "—";
            },
        },
        {
            index: 5,
            id: "actions",
            label: "Actions",
            sortable: false,
            render: (_, { data: row }) => {
                return (
                    <>
                        <Tooltip title="View details">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRowClickHandler(row);
                                }}
                            >
                                <VisibilityIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Apply payment">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onApplyClickHandler(row);
                                }}
                            >
                                <AssignmentTurnedInIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                );
            },
        },
    ];

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <Page className="cp-payments-page">
                <div className="cp-payments-filters">
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Client Name"
                                value={filters.clientFullName}
                                onChange={(e) => onFilterChange("clientFullName", e.target.value)}
                                fullWidth
                                size="small"
                                variant="outlined"
                                margin="dense"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                label="Status"
                                value={filters.status}
                                onChange={(e) => onFilterChange("status", e.target.value)}
                                fullWidth
                                size="small"
                                variant="outlined"
                                margin="dense"
                            >
                                <MenuItem value="">All</MenuItem>
                                {PAYMENT_STATUSES.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                label="Application"
                                value={filters.applicationStatus}
                                onChange={(e) => onFilterChange("applicationStatus", e.target.value)}
                                fullWidth
                                size="small"
                                variant="outlined"
                                margin="dense"
                            >
                                <MenuItem value="">All</MenuItem>
                                {APPLICATION_STATUSES.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                label="Payment ID"
                                value={filters.paymentId}
                                onChange={(e) => onFilterChange("paymentId", e.target.value)}
                                fullWidth
                                size="small"
                                variant="outlined"
                                margin="dense"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className="cp-payments-filters__toggle">
                                <Tooltip title={showMoreFilters ? "Less filters" : "More filters"}>
                                    <IconButton size="small" onClick={() => setShowMoreFilters((prev) => !prev)}>
                                        {showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </Grid>
                    </Grid>
                    <Collapse in={showMoreFilters}>
                        <Grid container spacing={2} alignItems="flex-end" style={{ marginTop: 4 }}>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    label="Client ID"
                                    value={filters.clientId}
                                    onChange={(e) => onFilterChange("clientId", e.target.value)}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    margin="dense"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    label="Amount From"
                                    value={filters.amountPaidUsdFrom}
                                    onChange={(e) => onFilterChange("amountPaidUsdFrom", e.target.value)}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    margin="dense"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    label="Amount To"
                                    value={filters.amountPaidUsdTo}
                                    onChange={(e) => onFilterChange("amountPaidUsdTo", e.target.value)}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    margin="dense"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KeyboardDateTimePicker
                                    label="Date From"
                                    value={filters.dateFrom}
                                    onChange={(date) => onFilterChange("dateFrom", date)}
                                    format="MM/DD/YYYY HH:mm"
                                    fullWidth
                                    size="small"
                                    inputVariant="outlined"
                                    margin="dense"
                                    clearable
                                    KeyboardButtonProps={{ size: "small" }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KeyboardDateTimePicker
                                    label="Date To"
                                    value={filters.dateTo}
                                    onChange={(date) => onFilterChange("dateTo", date)}
                                    format="MM/DD/YYYY HH:mm"
                                    fullWidth
                                    size="small"
                                    inputVariant="outlined"
                                    margin="dense"
                                    clearable
                                    KeyboardButtonProps={{ size: "small" }}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                    <div className="cp-payments-filters__actions">
                        <Button secondary small type="button" onClick={onSearchHandler} loading={isLoading}>
                            <Icon name="search" size="md" color="white" />
                            <span>Search</span>
                        </Button>
                        <Button link secondary small onClick={onClearFiltersHandler}>
                            Clear
                        </Button>
                    </div>
                </div>
                <div className="layout cp-payments-table">
                    {isLoading && <LoadingPage />}
                    {!isLoading && (
                        <EnhancedTable
                            headCells={headCells}
                            data={data}
                            defaultOrder={{ order: "desc", orderBy: "paymentCreationDatetimeString" }}
                            keyColumnName="id"
                            onRowClick={onRowClickHandler}
                            onRequestData={getCpPayments}
                            pageableData={pageableData}
                            defaultRowsPerPage={15}
                            filterable={false}
                            deletable={false}
                            selectable={false}
                        />
                    )}
                </div>
                <ApplyPaymentDialog
                    open={applyDialogOpen}
                    onClose={onApplyDialogClose}
                    payment={applyPayment}
                    onApplySuccess={onApplySuccess}
                />
            </Page>
        </MuiPickersUtilsProvider>
    );
}

export default ClientPortalPayments;
